import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

interface Payment {
  id?: string;
  date: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  description?: string;
  orderId?: string;
  clientName?: string;
  beneficiaryAccountNo?: string;
  transactionType?: string;
  utrNumber?: string;
} 

interface RiderPaymentHistoryTabProps {
  payments: Payment[];
  loading?: boolean;
}

const RiderPaymentHistoryTab: React.FC<RiderPaymentHistoryTabProps> = ({
  payments,
  loading = false,
}) => {

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading payment history...</Typography>
      </Box>
    );
  }

  if (payments.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No payment history available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Payment transactions will appear here once available.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payment History
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* <TableCell>Payment ID</TableCell> */}
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Beneficiary Account</TableCell>
              <TableCell>Txn Type</TableCell>
              <TableCell>UTR</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id} hover>
                {/* <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {payment.id}
                  </Typography>
                </TableCell> */}
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(payment.date)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {payment.description || '-'}
                  </Typography>
                  {payment.orderId && (
                    <Typography variant="caption" color="text.secondary">
                      Order: {payment.orderId}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{payment.clientName || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{payment.beneficiaryAccountNo || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{payment.transactionType || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{payment.utrNumber || '-'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={'text.primary'}
                  >
                    {formatAmount(payment.amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    color={getStatusColor(payment.status) as any}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RiderPaymentHistoryTab;
